import conversationApi, { IIdsConversations, IPaginationMessage } from '../api/conversationApi';
import messageApi from '../api/messageApi';
import * as messageUtils from '../functions/messageUtils';
import { removePrefixAndSuffix } from '../functions/messageUtils';
import { IApiResponse } from '../interfaces/IApiResponse';
import { IConversation } from '../interfaces/IConversation';
import { IChatMessage } from '../interfaces/IMessage';
import { IUserLoginInfo } from '../interfaces/IUser';
import chatMessage from '../models/chatMessage';
import Conversation from '../models/conversation';
import conversationModel, { IPeerVcard } from '../models/conversation';
import ConversationStatus from '../models/conversationStatus';
import Photo from '../models/photo';

interface Message {
  from: string;
  to: string;
  user?: IPeerVcard;
  bySelf?: boolean;
  ownerDelete?: number;
  buddyDelete?: number;
  [key: string]: any;
}

interface ConversationParams {
  [key: string]: any;
}

interface ExtraParam {
  [key: string]: any;
}

class ConversationService {
  static attachUserToMessageAndMarkIfDeleted(loginUser: IUserLoginInfo, peerUser: IPeerVcard, messages: Message[]): Message[] {
    const processedData = messages.map(value => {
      const message = Object.assign(new chatMessage(), value);
      if (!message.user) {
        if (messageUtils.removePrefixAndSuffix(message.from) === loginUser.xmppId) {
          message.user = loginUser as unknown as IPeerVcard & IUserLoginInfo;
        } else if (messageUtils.removePrefixAndSuffix(message.to) === peerUser.xmppId) {
          message.user = peerUser;
        }
      }
      message.isDelete = !!(
        (message.bySelf && value?.ownerDelete) ||
        0 > 0 ||
        (!message.bySelf && ((value?.ownerDelete || 0) > 0 || (value?.buddyDelete || 0) > 0))
      );
      return message;
    });
    return processedData;
  }

  deleteConversation(receiverXmppid: string): Promise<IApiResponse> {
    receiverXmppid = messageUtils.addPrefixAndSuffix(receiverXmppid);
    return conversationApi.deleteConversation(receiverXmppid);
  }

  deleteListConversations(idsConversation: IIdsConversations): Promise<IApiResponse> {
    return conversationApi.deleteListConversations(idsConversation);
  }

  deleteListConversationGroup(idsConversation: IIdsConversations): Promise<IApiResponse> {
    return conversationApi.deleteListConversationGroup(idsConversation);
  }

  getConversations(params?: ConversationParams): Promise<IApiResponse> {
    return conversationApi.getConversations(params).then(res => {
      let data: IConversation[] = [];
      try {
        if (res.success) {
          data = conversationModel.mappingObjects(res.data.items);
          res.data.items = data.filter(x => x.id && x.peerVcard.id > 0);
        } else {
          console.log('ERROR: conversationService/getConversations:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/getConversations:', e);
        throw new Error('failed');
      }
    });
  }

  getNewConversations(lastSynchAt: string, isGetNewConversation: boolean): Promise<IApiResponse> {
    return conversationApi.getNewConversations(lastSynchAt, isGetNewConversation).then(res => {
      let data: IConversation[] = [];
      try {
        if (res.success) {
          data = conversationModel.mappingObjects(res.data.items);
          res.data.items = data.filter(x => x.id && x.peerVcard.id > 0);
        } else {
          console.log('ERROR: conversationService/getNewConversations:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/getNewConversations:', e);
        throw new Error('failed');
      }
    });
  }

  getConversationsPined(params: ConversationParams): Promise<IApiResponse> {
    return conversationApi.getConversationsPined(params).then(res => {
      let data: IConversation[] = [];
      try {
        if (res.success && res.data && res.data.items) {
          data = conversationModel.mappingObjects(res.data.items);
          res.data.items = data.filter(x => x.id && x.peerVcard.id > 0);
        } else {
          console.log('ERROR: conversationService/getConversationsPined:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/getConversationsPined:', e);
        throw new Error('failed');
      }
    });
  }

  deleteMessage(mgsId: string, isLastConversationMessage: boolean, prevChatMessage: Message, isGroup = false): Promise<IApiResponse> {
    const params: { isGroup: boolean; prevMessageId?: string } = {
      isGroup,
    };
    if (isLastConversationMessage && prevChatMessage) {
      params.prevMessageId = prevChatMessage.id;
    }
    return conversationApi.deleteMessage(mgsId, params).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/deleteMessage:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/deleteMessage:', e);
        throw new Error('failed');
      }
    });
  }

  getConversationsDetails(
    receiverXmppid: string,
    loginUser: IUserLoginInfo,
    peerUser: IPeerVcard,
    params: ConversationParams
  ): Promise<IApiResponse> {
    if (peerUser && !peerUser.isGroup) {
      receiverXmppid = messageUtils.addPrefixAndSuffix(receiverXmppid);
    }
    return conversationApi.getConversationsDetails(receiverXmppid, params).then(res => {
      let data: IChatMessage[] = [];
      try {
        if (res && res.success && res.data && res.data.items) {
          const processedData = ConversationService.attachUserToMessageAndMarkIfDeleted(loginUser, peerUser, res.data.items);
          data = chatMessage.mappingObjects(processedData);
          res.data.items = data;
        } else {
          console.log('ERROR: conversationService/getConversationsDetails:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/getConversationsDetails:', e);
        throw new Error('failed');
      }
    });
  }

  pinOrUnpinConversation({
    pinned,
    xmppId,
    isGroupChat,
  }: {
    pinned: boolean;
    xmppId: string;
    isGroupChat: boolean;
  }): Promise<IApiResponse> {
    xmppId = !isGroupChat ? messageUtils.addPrefixAndSuffix(xmppId) : xmppId;
    return conversationApi.pinOrUnpinConversation({ pinned, xmppId }).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/pinOrUnpinConversation:', res);
          return { success: res.data.success, message: res.data.message || '' };
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/pinOrUnpinConversation:', e);
        throw new Error('failed');
      }
    });
  }

  getConversationUnreadCount(xmppId: string): Promise<IApiResponse> {
    xmppId = messageUtils.addPrefixAndSuffix(xmppId);
    const params = { exceptXmppId: xmppId };
    return conversationApi.getConversationUnreadCount(params).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/getConversationUnreadCount:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/getConversationUnreadCount:', e);
        throw new Error('failed');
      }
    });
  }

  resetConversationUnreadCount(peerId: string, buddyXmppId?: string): Promise<IApiResponse> {
    let params = { buddyId: peerId, buddyXmppId: '' };
    if (buddyXmppId) {
      params = {
        buddyId: `${0}`,
        buddyXmppId,
      };
    }
    return conversationApi.resetConversationUnreadCount(params).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/resetConversationUnreadCount:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/resetConversationUnreadCount:', e);
        throw new Error('failed');
      }
    });
  }

  checkIfCanSendMessage(userId: number): Promise<IApiResponse> {
    return conversationApi.checkIfCanSendMessage(userId).then(res => {
      try {
        let data = { canSend: true, reason: '', remaining: 1000, canVideoCall: false, canSendPrivate: false };
        if (res.success) {
          data = {
            canSend: res && res.data.CanSend ? res.data.CanSend : false,
            reason: res && res.data ? res.data.Reason : '',
            remaining: res && res.data ? res.data.Remaining : null,
            canVideoCall: res && res.data.CanVideoCall ? res.data.CanVideoCall : false,
            canSendPrivate: res && res.data.canSendPrivate ? res.data.canSendPrivate : true,
          };
          res.data = data;
        } else {
          console.log('ERROR: conversationService/checkIfCanSendMessage:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/checkIfCanSendMessage:', e);
        throw new Error('failed');
      }
    });
  }

  getConversationStatus(userId: number, isMobileWeb: boolean): Promise<IApiResponse> {
    return conversationApi.checkIfCanSendMessage(userId).then(res => {
      try {
        if (res.success) {
          return {
            data: ConversationStatus.mappingObject(res.data, isMobileWeb),
            success: res.success,
            message: res.message,
          };
        }
        return { message: res.message, ...res };
      } catch (e) {
        console.log('ERROR: conversationService/getConversationStatus:', e);
        return { message: res.message, ...res };
      }
    });
  }

  getConversationBaseInfo(xmppId: string): Promise<IApiResponse> {
    xmppId = messageUtils.addPrefixAndSuffix(xmppId);
    return conversationApi.getConversationBaseInfo(xmppId).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/getConversationBaseInfo:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/getConversationBaseInfo:', e);
        throw new Error('failed');
      }
    });
  }

  getOrCreateConversation(xmppId: string): Promise<IApiResponse<Conversation>> {
    xmppId = messageUtils.addPrefixAndSuffix(xmppId);
    const conversation = { buddyXmppId: xmppId };
    return conversationApi.getOrCreateConversation(conversation).then(res => {
      try {
        if (res.success) {
          const data = conversationModel.mappingObject(res.data);
          res.data = data;
        } else {
          console.log('ERROR: conversationService/getOrCreateConversation:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/getOrCreateConversation:', e);
        throw new Error('failed');
      }
    });
  }

  notifyMessageSentGroupChat(message: string, roomJid: string, extraParam?: ExtraParam): Promise<IApiResponse> {
    return messageApi.notifyMessageSentGroupChat(message, roomJid, extraParam).then(res => {
      try {
        if (!res.success || !res.data) {
          console.log('ERROR: conversationService/notifyMessageSentGroupChat:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/notifyMessageSentGroupChat:', e);
        throw new Error('failed');
      }
    });
  }

  notifyMessageSent(
    receiverId: string,
    message: string,
    notificationType: number,
    conversationKey: string,
    extraParam?: ExtraParam
  ): Promise<IApiResponse> {
    return messageApi.notifyMessageSent(receiverId, message, notificationType, conversationKey, extraParam).then(res => {
      try {
        if (!res.success || !res.data) {
          console.log('ERROR: conversationService/notifyMessageSent:', res.message);
        }

        return {
          success: res.success,
          data: res.data && res.data.Authorisation ? ConversationStatus.mappingObject(res.data.Authorisation) : {},
          rawResponse: {
            ...res,
          },
        };
      } catch (e) {
        console.log('ERROR: conversationService/notifyMessageSent:', e);
        throw new Error('failed');
      }
    });
  }

  updateMessageStatus(msgId: string, status: string): Promise<IApiResponse> {
    return messageApi.updateMessageStatus(msgId, status).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/updateMessageStatus:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/updateMessageStatus:', e);
        throw new Error('failed');
      }
    });
  }

  updateStatementRead(params: ConversationParams): Promise<IApiResponse> {
    return conversationApi.updateStatementRead(params).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/updateStatementRead:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/updateStatementRead:', e);
        throw new Error('failed');
      }
    });
  }

  patchIgnoreConversation(id: string, isIgnored: boolean): Promise<IApiResponse> {
    return conversationApi.patchIgnoreConversation(id, isIgnored).then(res => {
      try {
        if (!res.success) {
          console.log('ERROR: conversationService/patchIgnoreConversation:', res.message);
        }
        return res;
      } catch (e) {
        console.log('ERROR: conversationService/patchIgnoreConversation:', e);
        throw 'failed';
      }
    });
  }

  getSharedMedia(xmppId: string, isGroup: boolean, { lastSynchAt, pageSize }: IPaginationMessage): Promise<IApiResponse> {
    const receiverXmppid = isGroup ? xmppId : messageUtils.addPrefixAndSuffix(xmppId);
    return conversationApi.getSharedMedia(receiverXmppid, { lastSynchAt, pageSize }).then(res => {
      try {
        return {
          success: res.success,
          data: {
            ...res.data,
            items: res.data.items.map((i: any) => ({
              ...i,
              fromUserId: Number(removePrefixAndSuffix(i.from)),
            })),
          },
        };
      } catch (e) {
        console.log('ERROR: conversationService/getSharedMedia:', e);
        throw 'failed';
      }
    });
  }

  getSharedMediaByDirection(
    xmppId: string,
    isGroup: boolean,
    pageSize: number,
    referenceMediaTime: string,
    direction: string
  ): Promise<IApiResponse> {
    const receiverXmppid = isGroup ? xmppId : messageUtils.addPrefixAndSuffix(xmppId);
    return conversationApi.getSharedMediaByDirection(receiverXmppid, pageSize, referenceMediaTime, direction).then(res => {
      try {
        return { success: res.success, data: res.data };
      } catch (e) {
        console.log('ERROR: conversationService/getSharedMediaByDirection:', e);
        throw 'failed';
      }
    });
  }

  deleteSharedMedias(xmppId: string, isGroup: boolean, mediaIds: string[]): Promise<IApiResponse> {
    const receiverXmppid = isGroup ? xmppId : messageUtils.addPrefixAndSuffix(xmppId);
    return conversationApi.deleteSharedMedia(receiverXmppid, mediaIds).then(res => {
      try {
        return { success: res.success, data: res.data };
      } catch (e) {
        console.log('ERROR: conversationService/deleteSharedMedias:', e);
        throw 'failed';
      }
    });
  }

  createUserPhotoBanner(convId: number, eventId: number, file: File): Promise<IApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ConversationID', `${convId}`);
    formData.append('EventID', `${eventId}`);
    return conversationApi.createUserPhotoBanner(formData).then(res => {
      try {
        return { success: res.success, data: Photo.mappingObject(res.data.items[0]) };
      } catch (e) {
        console.log('ERROR: conversationService/createUserPhotoBanner:', e);
        throw 'failed';
      }
    });
  }
  sendEventGroupChatMassMsg(params: { eventId: number | string; receivers: string[]; message: string }): Promise<IApiResponse> {
    return conversationApi.sendMassMsgEventGroupChat(params).then(res => {
      try {
        return { success: res.success };
      } catch (e) {
        console.log('ERROR: conversationService/sendEventGroupChatMassMsg:', e);
        throw 'failed';
      }
    });
  }
  getUsersStatus(uIds: number[]): Promise<IApiResponse> {
    if (!uIds || uIds?.length === 0) return Promise.resolve({ success: true, data: [] });
    return conversationApi.getUsersStatus(uIds).then(res => {
      try {
        return { success: res.success, data: res.data.items };
      } catch (e) {
        console.log('ERROR: conversationService/getUsersStatus:', e);
        throw 'failed';
      }
    });
  }
}

export default new ConversationService();
